import { useCallback, useEffect, useRef, useState } from "react";
import { useGetAllSimplifiedSse } from "./useGetAllSimplifiedSse";
import SynchronizationService from "../../../services/synchronizationService";
import {
  getSynchronizationLastStatus,
  isIncrementalAllowedByStatus,
} from "../../../helpers/synchronizations";

// Mesmo teto de segurança usado em Reports/standard.jsx (INCREMENTAL_WATCH_MAX_MS) — pior caso
// razoável para uma carga Bling (Wave1 + Wave2 + RemainingCore) nunca chegar a um status terminal.
const DEFAULT_MAX_WATCH_MS = 15 * 60 * 1000;
const SYNC_REQUEST_PAYLOAD = { page: 1, pageSize: 1000 };

const extractSyncsFromPayload = (payload) => {
  const normalizedPayload =
    payload?.response || payload?.Response || payload?.data || payload?.Data || payload;

  return Array.isArray(normalizedPayload)
    ? normalizedPayload
    : Array.isArray(normalizedPayload?.data)
      ? normalizedPayload.data
      : Array.isArray(normalizedPayload?.Data)
        ? normalizedPayload.Data
        : Array.isArray(normalizedPayload?.response)
          ? normalizedPayload.response
          : Array.isArray(normalizedPayload?.Response)
            ? normalizedPayload.Response
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload?.Data)
                ? payload.Data
                : Array.isArray(payload?.response)
                  ? payload.response
                  : Array.isArray(payload?.Response)
                    ? payload.Response
                    : [];
};

const normalizeSyncFromPayloadItem = (sync) => {
  const normalizedSync = sync?.response || sync?.Response || sync;
  return SynchronizationService.transformDataFromBackend(normalizedSync);
};

/**
 * Acompanhamento ao vivo (via SSE de GET /sync/getAllSimplified, o mesmo usado em
 * Reports/standard.jsx) de uma ou mais sincronizações disparadas manualmente, enquanto
 * estiverem em andamento. Generaliza o padrão de Reports/standard.jsx (watch temporário +
 * flag "já vi rodando" + teto de segurança) para telas de lista, onde mais de uma integração
 * pode estar sendo observada ao mesmo tempo.
 *
 * A conexão SSE só existe enquanto houver pelo menos uma integração observada — não é uma
 * assinatura permanente da tela (ver useGetAllSimplifiedSse: enabled=false encerra o stream).
 *
 * Não possui nenhum acoplamento a ERP específico — quem decide QUANDO chamar startWatching
 * (ex.: somente para Bling) é o chamador.
 */
export const useIncrementalLiveTracking = ({
  onUpdate,
  maxWatchMs = DEFAULT_MAX_WATCH_MS,
} = {}) => {
  const [watchedIds, setWatchedIds] = useState(() => new Set());
  const sawRunningRef = useRef(new Map());
  const timeoutsRef = useRef(new Map());
  const onUpdateRef = useRef(onUpdate);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const stopWatching = useCallback((syncId) => {
    const key = String(syncId);

    const timeoutId = timeoutsRef.current.get(key);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutsRef.current.delete(key);
    }
    sawRunningRef.current.delete(key);

    setWatchedIds((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const startWatching = useCallback(
    (syncId) => {
      if (!syncId) return;
      const key = String(syncId);

      // Dedupe: já observando esta integração — não reinicia nem duplica timer/conexão.
      if (timeoutsRef.current.has(key)) return;

      sawRunningRef.current.set(key, false);
      timeoutsRef.current.set(
        key,
        setTimeout(() => stopWatching(key), maxWatchMs),
      );
      setWatchedIds((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    },
    [maxWatchMs, stopWatching],
  );

  // Sai da tela: nenhum timer sobrevive ao unmount.
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      timeouts.clear();
    };
  }, []);

  useGetAllSimplifiedSse({
    enabled: watchedIds.size > 0,
    payload: SYNC_REQUEST_PAYLOAD,
    onSyncs: (payload) => {
      if (watchedIds.size === 0) return;

      const items = extractSyncsFromPayload(payload).map(normalizeSyncFromPayloadItem);

      watchedIds.forEach((key) => {
        const matched = items.find((item) => String(item?.id ?? "") === key);
        if (!matched) return;

        if (typeof onUpdateRef.current === "function") {
          onUpdateRef.current(matched);
        }

        // Parte 3: não encerrar só porque a primeira leitura já mostra um status "terminal" —
        // pode ser o resultado do ciclo ANTERIOR, ainda não sobrescrito pelo worker do Integrador.
        // Só paramos quando observamos Sincronizando/Pendente pelo menos uma vez e DEPOIS disso
        // chegamos a um terminal (Sucesso/Erro) — ou quando o teto de segurança expira.
        const isTerminal = isIncrementalAllowedByStatus(
          getSynchronizationLastStatus(matched),
        );

        if (!isTerminal) {
          sawRunningRef.current.set(key, true);
          return;
        }

        if (sawRunningRef.current.get(key)) {
          stopWatching(key);
        }
      });
    },
  });

  return { startWatching, stopWatching };
};
